import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '#/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center cursor-pointer justify-center gap-[8px] rounded-[9999px]  font-medium leading-[normal] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--primary-500)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ',
  {
    variants: {
      variant: {
        primary:
          'bg-[color:var(--primary-500)] text-[color:var(--neutral-900)] hover:bg-[color:var(--primary-600)]',
        outline:
          'bg-white border border-[color:var(--primary-600)] text-[color:var(--neutral-900)] hover:bg-[color:var(--primary-100)]',
      },
      size: {
        default: 'h-10 px-4 py-2 text-[16px] [&_svg]:text-[20px]',
        sm: 'h-9 px-3 text-[14px] [&_svg]:text-[18px]',
        lg: 'h-14 px-6 text-[16px] [&_svg]:text-[22px]',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
    },
  },
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
